import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import Modal from '../components/Modal';
import { daysRemaining, formatDate } from '../utils/dateFormat';
import '../styles/billing.css';

type Plan = 'trial' | 'friend' | 'active';

interface PlanInfo {
  plan: Plan;
  trialExpiresAt: string | null;
  siteLimit: number | null;
  storageGBPurchased: number;
  hasStripe: boolean;
}

const CheckIcon = () => (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default function Billing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageConfirmOpen, setStorageConfirmOpen] = useState(false);
  const [error, setError] = useState('');
  const [storageError, setStorageError] = useState('');

  const successParam = searchParams.get('success');
  const cancelledParam = searchParams.get('cancelled');

  // Clear query params after showing the banner
  useEffect(() => {
    if (successParam || cancelledParam) {
      const timer = setTimeout(() => setSearchParams({}, { replace: true }), 5000);
      return () => clearTimeout(timer);
    }
  }, [successParam, cancelledParam, setSearchParams]);

  const loadPlan = useCallback(() => {
    setLoading(true);
    setError('');
    let cancelled = false;
    api.getBillingPlan()
      .then(data => { if (!cancelled) setPlanInfo(data); })
      .catch(() => { if (!cancelled) setError("Couldn't load your billing info. Try refreshing the page."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(loadPlan, [loadPlan]);

  const handleUpgrade = async () => {
    setError('');
    setActionLoading(true);
    try {
      const { url } = await api.createCheckoutSession();
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong — please try again.');
      setActionLoading(false);
    }
  };

  const handleManage = async () => {
    setError('');
    setActionLoading(true);
    try {
      const { url } = await api.createPortalSession();
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong — please try again.');
      setActionLoading(false);
    }
  };

  const handleAddStorage = async () => {
    setStorageConfirmOpen(false);
    setStorageError('');
    setStorageLoading(true);
    try {
      const result = await api.addStorage(1);
      setPlanInfo(prev => prev ? { ...prev, storageGBPurchased: result.storageGBPurchased } : prev);
    } catch (e) {
      setStorageError(e instanceof Error ? e.message : 'Something went wrong — please try again.');
    } finally {
      setStorageLoading(false);
    }
  };

  const isExpired = planInfo?.plan === 'trial' && planInfo.trialExpiresAt
    ? new Date(planInfo.trialExpiresAt) < new Date()
    : false;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Billing</h1>
      </div>

      {/* Success / cancelled banners */}
      {successParam === 'true' && (
        <div role="alert" className="billing-banner billing-banner-success">
          <CheckIcon />
          You're now on an active plan — welcome!
        </div>
      )}
      {cancelledParam === 'true' && (
        <div role="status" className="billing-banner billing-banner-info">
          Checkout cancelled. Your plan hasn't changed.
        </div>
      )}

      {error && (
        <div role="alert" aria-live="assertive" className="auth-error" style={{ marginBottom: 16 }}>
          {error}
          {!planInfo && (
            <button className="btn-text" onClick={loadPlan} style={{ marginLeft: 8 }}>
              Try again
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div role="status" aria-label="Loading your plan details" className="loading-state">
          <div className="spinner" aria-hidden="true" />
          <p>Loading your plan details…</p>
        </div>
      ) : planInfo ? (
        <div className="billing-layout">

          {/* Current plan card */}
          <div className="billing-card">
            <div className="billing-card-header">
              <div>
                <div className="billing-plan-label">Current plan</div>
                <div className="billing-plan-name">
                  {planInfo.plan === 'active' ? 'Active' : planInfo.plan === 'friend' ? 'Friend' : 'Trial'}
                </div>
              </div>
              <span className={`billing-plan-badge ${planInfo.plan === 'active' ? 'badge-active' : isExpired ? 'badge-expired' : 'badge-trial'}`}>
                {planInfo.plan === 'active' ? 'Subscribed' : isExpired ? 'Expired' : 'Free trial'}
              </span>
            </div>

            {planInfo.plan === 'trial' && planInfo.trialExpiresAt && (
              <div className={`billing-trial-info ${isExpired ? 'billing-trial-expired' : ''}`}>
                {isExpired
                  ? 'Your trial has expired. Subscribe to continue publishing.'
                  : `Your trial expires on ${formatDate(planInfo.trialExpiresAt)} — ${daysRemaining(planInfo.trialExpiresAt)} days remaining.`
                }
              </div>
            )}

            <div className="billing-plan-limits">
              <div className="billing-limit-item">
                <span className="billing-limit-label">Sites</span>
                <span className="billing-limit-value">
                  {planInfo.plan === 'trial'
                    ? `${planInfo.siteLimit ?? 1} (trial)`
                    : planInfo.plan === 'friend'
                      ? planInfo.siteLimit ?? 'Unlimited'
                      : 'Unlimited'}
                </span>
              </div>
              <div className="billing-limit-item">
                <span className="billing-limit-label">Price per site</span>
                <span className="billing-limit-value">
                  {planInfo.plan === 'active' ? '$5 / mo' : planInfo.plan === 'friend' ? 'Complimentary' : '—'}
                </span>
              </div>
              <div className="billing-limit-item">
                <span className="billing-limit-label">Included storage</span>
                <span className="billing-limit-value">500 MB / site</span>
              </div>
              <div className="billing-limit-item">
                <span className="billing-limit-label">Custom domains</span>
                <span className="billing-limit-value">{planInfo.plan !== 'trial' ? 'Yes' : 'No'}</span>
              </div>
            </div>

            {planInfo.hasStripe && (
              <button
                className="btn btn-secondary"
                onClick={handleManage}
                disabled={actionLoading}
                aria-busy={actionLoading}
              >
                {actionLoading ? 'Opening portal…' : 'Manage subscription'}
              </button>
            )}
          </div>

          {/* Storage add-on — shown for active subscribers */}
          {planInfo.plan === 'active' && (
            <div className="billing-storage-card">
              <div className="billing-storage-header">
                <div className="billing-storage-title">Extra storage</div>
                <div className="billing-storage-subtitle">
                  500 MB is included with each site. Add more if you need it.
                </div>
              </div>
              <div className="billing-storage-row">
                <div>
                  <span className="billing-storage-amount">
                    {planInfo.storageGBPurchased > 0
                      ? `${planInfo.storageGBPurchased} GB purchased`
                      : 'No extra storage purchased'}
                  </span>
                  {planInfo.storageGBPurchased > 0 && (
                    <span className="billing-storage-cost">
                      {' '}· ${planInfo.storageGBPurchased * 5} / mo
                    </span>
                  )}
                </div>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setStorageConfirmOpen(true)}
                  disabled={storageLoading}
                  aria-busy={storageLoading}
                >
                  {storageLoading ? 'Adding…' : '+ 1 GB'}
                </button>
              </div>
              {storageError && (
                <div role="alert" className="billing-storage-error">{storageError}</div>
              )}
              <div className="billing-storage-note">$5 / GB / mo — billed monthly, prorated from today.</div>
            </div>
          )}

          {/* Upgrade card — shown for trial users */}
          {planInfo.plan === 'trial' && (
            <div className="billing-upgrade-card">
              <div className="billing-upgrade-header">
                <div className="billing-upgrade-title">Subscribe</div>
                <div className="billing-upgrade-subtitle">
                  $5 / site / month. Create as many sites as you need.
                </div>
              </div>

              <ul className="billing-features">
                {[
                  'Unlimited sites at $5 / site / month',
                  'Custom domain per site',
                  '500 MB media storage per site',
                  'Extra storage from $5 / GB / month',
                  'Contact form with email notifications',
                  'Visitor analytics',
                  'Version history & rollback',
                ].map((feature) => (
                  <li key={feature} className="billing-feature-item">
                    <span className="billing-feature-check"><CheckIcon /></span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                className="btn btn-primary btn-full"
                onClick={handleUpgrade}
                disabled={actionLoading}
                aria-busy={actionLoading}
              >
                {actionLoading ? 'Opening checkout…' : 'Start subscription'}
              </button>

              <p className="billing-fine-print">
                Pricing shown at checkout — cancel anytime.
              </p>
            </div>
          )}

        </div>
      ) : null}

      {/* Storage purchase confirmation */}
      <Modal
        open={storageConfirmOpen}
        onClose={() => setStorageConfirmOpen(false)}
        title="Add extra storage"
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => setStorageConfirmOpen(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleAddStorage}>
              Add 1 GB for $5 / month
            </button>
          </>
        }
      >
        <p>
          Add 1 GB of extra storage for <strong>$5 / month</strong>?
        </p>
        <p>
          {planInfo && planInfo.storageGBPurchased > 0
            ? `Your extra storage will go from ${planInfo.storageGBPurchased} GB ($${planInfo.storageGBPurchased * 5} / month) to ${planInfo.storageGBPurchased + 1} GB ($${(planInfo.storageGBPurchased + 1) * 5} / month), `
            : 'It will be added to your subscription, '}
          billed monthly and prorated from today. You can remove it any time from
          the subscription portal via “Manage subscription”.
        </p>
      </Modal>
    </div>
  );
}
